import { useState } from 'react';
import type { JSX } from 'react';
import { ChevronDown } from 'lucide-react';
import type { WireArtifact } from '@logistics/contracts';
import { Fact, StatusPill, Truncated, caption } from './parts';
import { vehicleFacts } from './vehicle-facts';

type Vehicle = Extract<WireArtifact, { kind: 'vehicle' }>;
type Line = Vehicle['vehicles'][number];

/** Make, model and year as one line — only the parts the provider documented. */
function describe(line: Line): string | undefined {
  const parts = [line.year, line.make, line.model].filter((p): p is string => p !== undefined);
  return parts.length === 0 ? undefined : parts.join(' ');
}

/** One truck as a card, a fleet as a list whose rows open. */
export function VehicleArtifact({ artifact }: { artifact: Vehicle }): JSX.Element {
  const only = artifact.vehicles.length === 1 ? artifact.vehicles[0] : undefined;
  return (
    <div className="flex flex-col gap-2.5">
      {only !== undefined && <VehicleCard line={only} />}
      {only === undefined && artifact.vehicles.map((line, i) => <VehicleRow key={i} line={line} />)}
      {artifact.vehicles.length === 0 && <p className="text-mute text-[13px]">No trucks.</p>}
      {artifact.truncated && <Truncated what={`${artifact.vehicles.length} trucks`} />}
    </div>
  );
}

function VehicleCard({ line }: { line: Line }): JSX.Element {
  const facts = vehicleFacts(line, describe(line));
  return (
    <div className="flex flex-col gap-3.5">
      <div className="flex items-center justify-between gap-3">
        <span className="min-w-0 font-mono text-xl font-medium tracking-[-.01em] [overflow-wrap:anywhere]">
          {line.unit}
        </span>
        {line.status !== undefined && <StatusPill status={line.status} />}
      </div>
      {facts.length > 0 && (
        <div className="grid grid-cols-2 gap-2.5">
          {facts.map((f, i) => (
            <Fact key={i} k={f.k} v={f.v} />
          ))}
        </div>
      )}
    </div>
  );
}

function VehicleRow({ line }: { line: Line }): JSX.Element {
  const [open, setOpen] = useState(false);
  const detail = describe(line) ?? line.vin ?? line.plate;
  const facts = vehicleFacts(line, describe(line));
  const opens = facts.length > 0;
  return (
    <div className="border-line bg-surf rounded-xl border">
      <button
        type="button"
        className="flex w-full items-center gap-3 px-3.5 py-2.5 text-left disabled:cursor-default"
        aria-expanded={open}
        disabled={!opens}
        onClick={() => setOpen((was) => !was)}
      >
        <span className="bg-surf2 grid h-6 w-[34px] flex-none place-items-center rounded-md font-mono text-[10px] font-medium">
          TRK
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate font-mono text-[12.5px] font-medium">{line.unit}</div>
          {detail !== undefined && (
            <div className={`${caption} mt-0.5 truncate`}>{detail.toUpperCase()}</div>
          )}
        </div>
        {line.status !== undefined && (
          <span className="text-mute max-w-[88px] flex-none truncate text-[11.5px]">
            {line.status}
          </span>
        )}
        {opens && (
          <ChevronDown
            aria-hidden
            className={`text-mute size-4 flex-none transition-transform ${open ? 'rotate-180' : ''}`}
          />
        )}
      </button>
      {open && (
        <dl className="border-line m-0 grid grid-cols-1 gap-x-4 gap-y-2 border-t px-3.5 py-3 sm:grid-cols-2">
          {facts.map((f, i) => (
            <div key={i} className="min-w-0">
              <dt className={`${caption} truncate`} title={f.k}>
                {f.k.toUpperCase()}
              </dt>
              <dd className="m-0 mt-0.5 text-[12.5px] [overflow-wrap:anywhere]">{f.v}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}
